import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, UserPlus, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

interface Props {
  open: boolean;
  onOpenChange: (v: boolean) => void;
}

const AddSystemStaffDialog = ({ open, onOpenChange }: Props) => {
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", password: "", phone: "",
    role: "support", department: "general", notes: "",
  });

  const u = (key: string, val: string) => setForm({ ...form, [key]: val });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.password) return;
    setLoading(true);

    const { data, error } = await supabase.functions.invoke("manage-system-staff", {
      body: { action: "create", ...form },
    });

    setLoading(false);
    if (error || data?.error) {
      toast.error(data?.error || error?.message || "خطأ في إنشاء الحساب");
      return;
    }
    toast.success("تم إضافة الموظف وإنشاء حسابه بنجاح ✅");
    queryClient.invalidateQueries({ queryKey: ["system-staff"] });
    onOpenChange(false);
    setForm({ full_name: "", email: "", password: "", phone: "", role: "support", department: "general", notes: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5 text-primary" />
            إضافة موظف نظام جديد
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>الاسم الكامل *</Label>
            <Input value={form.full_name} onChange={(e) => u("full_name", e.target.value)} required placeholder="الاسم الكامل" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>الدور *</Label>
              <Select value={form.role} onValueChange={(v) => u("role", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="support">دعم فني</SelectItem>
                  <SelectItem value="call_center">مركز اتصال</SelectItem>
                  <SelectItem value="accountant">محاسب</SelectItem>
                  <SelectItem value="developer">مطوّر</SelectItem>
                  <SelectItem value="supervisor">مشرف</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>القسم</Label>
              <Select value={form.department} onValueChange={(v) => u("department", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">عام</SelectItem>
                  <SelectItem value="technical">تقني</SelectItem>
                  <SelectItem value="financial">مالي</SelectItem>
                  <SelectItem value="operations">عمليات</SelectItem>
                  <SelectItem value="customer_service">خدمة العملاء</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label>رقم الهاتف</Label>
            <Input value={form.phone} onChange={(e) => u("phone", e.target.value)} placeholder="05xxxxxxxx" dir="ltr" />
          </div>

          {/* Account Credentials */}
          <div className="border rounded-lg p-3 space-y-3 border-primary/30 bg-primary/5">
            <p className="text-sm font-medium text-primary">بيانات حساب الدخول</p>
            <div>
              <Label>البريد الإلكتروني *</Label>
              <Input type="email" value={form.email} onChange={(e) => u("email", e.target.value)} required placeholder="staff@example.com" dir="ltr" />
            </div>
            <div>
              <Label>كلمة المرور *</Label>
              <div className="relative">
                <Input
                  type={showPw ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => u("password", e.target.value)}
                  required minLength={6} placeholder="******" dir="ltr"
                />
                <button type="button" className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">6 أحرف على الأقل</p>
            </div>
          </div>

          <div>
            <Label>ملاحظات</Label>
            <Textarea value={form.notes} onChange={(e) => u("notes", e.target.value)} rows={2} placeholder="ملاحظات إضافية..." />
          </div>

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : <UserPlus className="w-4 h-4 ml-2" />}
            إضافة وإنشاء حساب
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddSystemStaffDialog;
